import { useCallback, useState } from "react";

export type AsyncStatus = "idle" | "loading" | "success" | "error";

export interface AsyncTaskState<TData, TError> {
  status: AsyncStatus;
  loading: boolean;
  data?: TData;
  error?: TError;
}

export interface UseAsyncTaskOptions<TArgs extends unknown[], TData, TError> {
  task: (...args: TArgs) => Promise<TData>;
  /**
   * Valor inicial de `data` e de `reset()`.
   * Para objetos/arrays, use referência estável (ex.: constante de módulo);
   * um literal novo a cada render (ex.: `[]`, `{}`) faz `reset` mudar de identidade
   * e pode causar loop em `useEffect` que dependa de `reset`.
   */
  initialData?: TData;
  /**
   * Permite adaptar o erro bruto para um formato específico.
   * Exemplo: converter qualquer erro em HttpError ou em string de mensagem.
   */
  mapError?: (error: unknown) => TError;
}

export interface UseAsyncTaskResult<
  TArgs extends unknown[],
  TData,
  TError,
> extends AsyncTaskState<TData, TError> {
  run: (...args: TArgs) => Promise<TData>;
  reset: () => void;
}

/**
 * Hook genérico para orquestrar chamadas assíncronas.
 *
 * Responsabilidades:
 * - Controlar estados de loading, sucesso e erro de uma Promise.
 * - Centralizar try/catch e captura de erro.
 * - Não impor nenhuma UI; o componente decide como usar status/loading/erro.
 */
export function useAsyncTask<TArgs extends unknown[], TData, TError = unknown>(
  options: UseAsyncTaskOptions<TArgs, TData, TError>,
): UseAsyncTaskResult<TArgs, TData, TError> {
  const { task, initialData, mapError } = options;

  const [state, setState] = useState<AsyncTaskState<TData, TError>>({
    status: "idle",
    loading: false,
    data: initialData,
    error: undefined,
  });

  const run = useCallback(
    async (...args: TArgs): Promise<TData> => {
      setState((prev) => ({
        ...prev,
        status: "loading",
        loading: true,
        error: undefined,
      }));

      try {
        const result = await task(...args);

        setState({
          status: "success",
          loading: false,
          data: result,
          error: undefined,
        });

        return result;
      } catch (error) {
        const mappedError = (mapError ? mapError(error) : (error as TError)) as
          | TError
          | undefined;

        setState((prev) => ({
          status: "error",
          loading: false,
          // Mantém o último data conhecido, sem capturar state por closure
          data: prev.data,
          error: mappedError,
        }));

        throw error;
      }
    },
    [task, mapError],
  );

  const reset = useCallback(() => {
    setState({
      status: "idle",
      loading: false,
      data: initialData,
      error: undefined,
    });
  }, [initialData]);

  return {
    ...state,
    run,
    reset,
  };
}
